import React from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Badge } from '../components/ui';

export interface VehiclePlateNumberDisplayOptions {
  /** ナンバープレート番号 */
  vehiclePlateNumber?: string;
  /** ナンバープレート番号なしフラグ */
  vehiclePlateNumberNone?: boolean;
  /** 表示サイズ */
  size?: 'sm' | 'md' | 'lg';
  /** 説明テキストを表示するか */
  showDescription?: boolean;
}

/**
 * ナンバープレート番号の表示を統一的に処理するユーティリティ関数
 * @param options 表示オプション
 * @returns 表示用のReactコンポーネント
 */
export function renderVehiclePlateNumber(
  options: VehiclePlateNumberDisplayOptions
): React.ReactNode {
  const {
    vehiclePlateNumber,
    vehiclePlateNumberNone = false,
    size = 'sm',
    showDescription = true,
  } = options;

  // ナンバープレート番号なしフラグが設定されている場合
  if (vehiclePlateNumberNone) {
    return (
      <Badge variant='secondary' size={size}>
        ナンバー取得予定なし
      </Badge>
    );
  }

  // ナンバープレート番号が未設定の場合
  if (!vehiclePlateNumber || vehiclePlateNumber.trim() === '') {
    return (
      <div>
        <Badge variant='warning' size={size} className={showDescription ? 'mb-2' : ''}>
          <ExclamationCircleIcon className='h-3 w-3 mr-1' />
          未設定
        </Badge>
        {showDescription && (
          <p className='text-xs text-gray-500 dark:text-gray-400'>
            引取日が近づくにつれて設定される可能性があります
          </p>
        )}
      </div>
    );
  }

  // ナンバープレート番号が設定されている場合
  return vehiclePlateNumber;
}

/**
 * プレーンテキストとしてナンバープレート番号を取得する関数
 * @param options 表示オプション
 * @returns プレーンテキスト文字列
 */
export function getVehiclePlateNumberText(options: VehiclePlateNumberDisplayOptions): string {
  const { vehiclePlateNumber, vehiclePlateNumberNone = false } = options;

  if (vehiclePlateNumberNone) {
    return 'ナンバー取得予定なし';
  }

  if (!vehiclePlateNumber || vehiclePlateNumber.trim() === '') {
    return '未設定';
  }

  return vehiclePlateNumber;
}

/**
 * ナンバープレート番号の状態を判定する関数
 * @param options 表示オプション
 * @returns 状態の種類
 */
export function getVehiclePlateNumberStatus(
  options: VehiclePlateNumberDisplayOptions
): 'set' | 'unset' | 'none' {
  const { vehiclePlateNumber, vehiclePlateNumberNone = false } = options;

  if (vehiclePlateNumberNone) {
    return 'none';
  }

  if (!vehiclePlateNumber || vehiclePlateNumber.trim() === '') {
    return 'unset';
  }

  return 'set';
}
